import { defineComponent, ref, watch, onMounted, inject } from 'vue';
import SSelectorMultiple from '@/components/stemdo-components/s-selector-multiple/SSelectorMultiple.vue';
import SSelector from '@/components/stemdo-components/s-selector/SSelector.vue';
import SButton from '@/components/stemdo-components/s-button/SButton.vue';
import TechnologyService from '@/sections/admin/entities/technology/technology.service';
import useVuelidate from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { ITechnologyScore } from '@/shared/model/technology-score.model';
import { useI18N } from '@/plugins/i18n';

export default defineComponent({
  props: {
    isOpen: {
      type: Boolean,
      required: true
    },
    comps: {
      type: Array as () => ITechnologyScore[],
      default: () => []
    },
    onCompsSave: {
      type: Function,
      required: true
    },
    onCloseModal: {
      type: Function,
      required: true
    }
  },
  components: {
    SSelectorMultiple,
    SSelector,
    SButton
  },
  setup(props) {
    const i18n = useI18N();
    const technologyService: TechnologyService = inject('technologyService');
    const compsForm = ref([]);
    const competenciesItems = ref([]);
    const selectedTechnologies = ref([]);
    const scoreOptions = ref(Array.from({ length: 7 }, (_, i) => ({ id: i + 1, desc: (i + 1).toString() })));
    const vuelidate = useVuelidate(() => compsForm.value.map(() => ({ score: { required } })), compsForm);

    onMounted(async () => {
      competenciesItems.value = (await technologyService.retrieve())
        .filter(item => {
          const key = `technologyMaster.list.${item.id}`;
          return i18n.t(key).toString() !== key;
        })
        .map(item => ({
          id: item.id,
          desc: item.name
        }));
    });

    watch(
      () => props.comps,
      value => {
        compsForm.value = value.map(comp => ({
          idTechnology: comp.idTechnology,
          score: comp.score
        }));
        selectedTechnologies.value = compsForm.value.map(comp => comp.idTechnology);
      },
      { immediate: true }
    );

    const addTechnology = (idTechnology: number) => {
      if (!competenciesItems.value.some(item => item.id === idTechnology)) {
        competenciesItems.value.push({ id: idTechnology, desc: `Technology ${idTechnology}` });
      }
      if (!compsForm.value.some(comp => comp.idTechnology === idTechnology)) {
        compsForm.value.push({ idTechnology, score: 1 });
      }
    };

    const updateCompetencies = (selectedCompetencies: number[]) => {
      selectedCompetencies.forEach(addTechnology);
      compsForm.value = compsForm.value.filter(comp => selectedCompetencies.includes(comp.idTechnology));
    };

    watch(selectedTechnologies, updateCompetencies);

    const getStars = (score: number) => {
      if (score <= 3) {
        return [1];
      } else if (score <= 5) {
        return [1, 2];
      } else if (score >= 6) {
        return [1, 2, 3];
      }
    };

    const getTitleBadge = (score: number) => {
      if (score <= 3) {
        return i18n.t('technologyMaster.tech-badges.tier.1');
      } else if (score <= 5) {
        return i18n.t('technologyMaster.tech-badges.tier.2');
      } else if (score >= 6) {
        return i18n.t('technologyMaster.tech-badges.tier.3');
      }
    };

    const getInfoBadge = (score: number, idTechnology: number) => {
      let translationKey;
      if (score <= 3) {
        translationKey = `technologyMaster.tech-badges.${idTechnology}.1`;
      } else if (score <= 5) {
        translationKey = `technologyMaster.tech-badges.${idTechnology}.2`;
      } else if (score >= 6) {
        translationKey = `technologyMaster.tech-badges.${idTechnology}.3`;
      }
      let translation = i18n.t(translationKey);
      if (translation === translationKey) {
        translation = i18n.t(translationKey.replace(idTechnology.toString(), '0'));
      }
      return translation;
    };

    const updateScore = (index, newValue) => {
      if (typeof newValue === 'number') {
        compsForm.value[index].score = newValue;
      } else {
        console.error('Invalid value passed to updateScore:', newValue);
      }
    };

    const getTechnologyName = idTechnology => {
      const tech = competenciesItems.value.find(item => item.id === idTechnology);
      return tech ? tech.desc : i18n.t('stemdoerDetail.compsModal.unknownTechnology');
    };

    const onSaveClick = async () => {
      if (compsForm.value.length === 0) {
        console.warn('No competencies selected. Save operation aborted.');
        return;
      }

      const result = await vuelidate.value.$validate();
      if (result) {
        const formattedComps = compsForm.value.map(comp => ({
          idTechnology: comp.idTechnology,
          score: comp.score
        }));
        await props.onCompsSave(formattedComps);
        closeModal();
      }
    };

    const closeModal = () => {
      props.onCloseModal();
    };

    const removeComp = index => {
      compsForm.value.splice(index, 1);
      selectedTechnologies.value = compsForm.value.map(comp => comp.idTechnology);
    };

    return {
      compsForm,
      competenciesItems,
      selectedTechnologies,
      scoreOptions,
      vuelidate,
      updateCompetencies,
      addTechnology,
      updateScore,
      getStars,
      getTitleBadge,
      getInfoBadge,
      getTechnologyName,
      onSaveClick,
      closeModal,
      removeComp
    };
  }
});
